import { z } from 'zod';
import { ColorSchema, about } from './schema_fields.ts';

/**
 * A background, border or title background meaning none at all (issues #11, #12), also where a
 * default would give one. A transparent colour is still a colour: a border of it takes its width.
 */
export const NONE = 'none';

export const ColorOrNoneSchema = z.union([ColorSchema, z.literal(NONE)]);

/** The gauge styles (docs/specs/gauge-styles.md); `ticks` is the legacy dial, the default. */
export const GAUGE_STYLES = ['ticks', 'arc', 'marker', 'needle', 'half', 'ring', 'rings', 'blocks', 'glow', 'bar', 'cells', 'thermometer', 'tank', 'bullet', 'dots', 'minimal'] as const;
export type GaugeStyle = (typeof GAUGE_STYLES)[number];
export const GaugeStyleSchema = z.enum(GAUGE_STYLES);
const SizeSchema = z.number().min(0);

/** Where a tile's icon goes (TS-10, issue #21): a corner or the middle of the top or bottom edge. */
export const ICON_POSITIONS = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'] as const;
export type IconPosition = (typeof ICON_POSITIONS)[number];

/** How the editor names them. */
export const ICON_POSITION_LABELS: Readonly<Record<IconPosition, string>> = {
    'top-left': 'Top left',
    'top-center': 'Top middle',
    'top-right': 'Top right',
    'bottom-left': 'Bottom left',
    'bottom-center': 'Bottom middle',
    'bottom-right': 'Bottom right',
};

/** How a tile's title or a value tile's number lines up across its room (TS-14, TS-15). */
export const ALIGNMENTS = ['left', 'center', 'right'] as const;
export type Alignment = (typeof ALIGNMENTS)[number];

/** How the editor names them. */
export const ALIGNMENT_LABELS: Readonly<Record<Alignment, string>> = {
    left: 'Left',
    center: 'Centre',
    right: 'Right',
};

/**
 * A tile's style (dashboard-editor spec §5.9, TS-1; T-2, T-3): each property left out takes the
 * dashboard's (`tileStyle`, TS-2), and that left out the default (@casa/tiles `DEFAULT_TILE_STYLE`).
 * Sizes are in CSS pixels. A tile type's own colours (a light's on and off, a value's colour) win
 * over the text colour (T-3).
 */
export const TileStyleSchema = z.object({
    background: ColorOrNoneSchema.optional().meta(about('Background', undefined, 'color')),
    padding: SizeSchema.optional().meta(about('Padding', 'room inside the tile, in pixels')),
    radius: SizeSchema.optional().meta(about('Corner radius', 'in pixels')),
    border: ColorOrNoneSchema.optional().meta(about('Border', undefined, 'color')),
    borderWidth: SizeSchema.optional().meta(about('Border width', 'in pixels')),
    title: ColorSchema.optional().meta(about('Title', 'the title and its icon', 'color')),
    titleHeight: SizeSchema.optional().meta(about('Title height', 'in pixels')),
    titleBackground: ColorOrNoneSchema.optional().meta(about('Title background', 'out to the edges of the tile', 'color')),
    /** The title in capitals, as the legacy tiles had it (TS-8, the owner); false: as typed. */
    titleUppercase: z.boolean().optional().meta(about('Title in capitals')),
    /** Where the title goes across its row (TS-14): centred, as the legacy tiles had it, when left out. */
    titleAlign: z.enum(ALIGNMENTS).optional().meta({ ...about('Title alignment', 'across its row'), 'x-labels': ALIGNMENT_LABELS }),
    text: ColorSchema.optional().meta(about('Text', 'where the tile has no colour of its own for it', 'color')),
    /** How a gauge is drawn (gauge-styles spec): a tile's own over the dashboard's over `ticks`. */
    gaugeStyle: GaugeStyleSchema.optional().meta(about('Gauge style', 'how a gauge or dial is drawn')),
    /** Where a value tile's number and its unit go across the tile (TS-15); a gauge stays in the middle. */
    valueAlign: z.enum(ALIGNMENTS).optional().meta({ ...about('Value alignment', 'a value tile\'s number and its unit; a gauge stays in the middle'), 'x-labels': ALIGNMENT_LABELS }),
    /** A value tile's caption (SR-8, SR-9): its text's height in percent of the title height, 60 by default. */
    captionSize: z.number().min(10).max(400).optional().meta(about('Caption size', 'in percent of the title height')),
    /** Grey by default (SR-9). */
    captionColor: ColorSchema.optional().meta(about('Caption colour', undefined, 'color')),
    /** Lined up as the number when left out (SR-9); centred under a gauge. */
    captionAlign: z.enum(ALIGNMENTS).optional().meta({ ...about('Caption alignment', 'as the value when left out'), 'x-labels': ALIGNMENT_LABELS }),
    /** Where the tile's icon is drawn, over everything and apart from the title (TS-10, issue #21). */
    iconPosition: z.enum(ICON_POSITIONS).optional().meta({
        ...about('Icon position', 'a corner, or the middle of the top or bottom edge; over the title and the content'),
        'x-labels': ICON_POSITION_LABELS,
    }),
    /** The icon's square, or its background's where it has one (TS-11). */
    iconSize: SizeSchema.optional().meta(about('Icon size', 'in pixels; with a background, the size of the background')),
    /** The title's colour when left out (TS-11). */
    iconColor: ColorSchema.optional().meta(about('Icon colour', 'the title\'s when left out', 'color')),
    iconBackground: ColorOrNoneSchema.optional().meta(about('Icon background', undefined, 'color')),
    iconRadius: SizeSchema.optional().meta(about('Icon corner radius', 'of its background, in pixels; half the icon size makes a circle')),
    /** Room between the icon and the tile's edges, beyond the tile's padding (TS-13). */
    iconMargin: SizeSchema.optional().meta(about('Icon margin', 'room between it and the tile\'s edges, beyond the padding, in pixels')),
    /** The tile's background, border and shadow shown, true by default; false hides them as set, for a tile laid over others (DS-8). */
    showBackground: z.boolean().optional().meta(about('Background, border and shadow', 'off: none of them, whatever they are set to; for a tile laid over others')),
    /** A drop shadow outside the tile's box (issue #35, DS-5): off by default; the other four ready when it is on. */
    shadow: z.boolean().optional().meta(about('Shadow')),
    shadowColor: ColorSchema.optional().meta(about('Shadow colour', 'with its opacity: #aarrggbb', 'color')),
    shadowX: z.number().optional().meta(about('Shadow across', 'in pixels; below 0 to the left')),
    shadowY: z.number().optional().meta(about('Shadow down', 'in pixels; below 0 upwards')),
    shadowBlur: SizeSchema.optional().meta(about('Shadow blur', 'in pixels')),
});

export type TileStyle = z.infer<typeof TileStyleSchema>;

/** The tile shapes a style can be made for (tile-styles.md TY-10); a style without one suits any. */
export const STYLE_SHAPES = ['wide', 'square', 'tall'] as const;
export type StyleShape = (typeof STYLE_SHAPES)[number];

/** Each shape's width over its height, on the screen. */
export const STYLE_SHAPE_RATIOS: Readonly<Record<StyleShape, number>> = { wide: 2, square: 1, tall: 0.5 };

/** The shape nearest to a tile `ratio` times as wide as high: the halfway points are √2 and 1/√2. */
export function nearestStyleShape(ratio: number) : StyleShape {
    if (ratio >= Math.SQRT2) {
        return 'wide';
    }

    return ratio <= Math.SQRT1_2 ? 'tall' : 'square';
}


